import React from 'react';
import { 
    Clock, Sparkles, BookOpen, 
    ArrowRight, X, CheckCircle2, CreditCard
} from 'lucide-react';

/**
 * SubscriptionAlertModal
 * Pop-up automático e inteligente com mensagens automáticas baseadas nos dias restantes.
 * Oferece as opções claras:
 * - "Ir para o Pagamento" (direciona para a tela de assinatura)
 * - "Deixar para depois" (dispensa o aviso pelo restante do dia)
 */
export const SubscriptionAlertModal = ({
    isOpen,
    type = 'trial_expiring', // 'trial_expiring' | 'plan_expiring' | 'read_only'
    daysLeft = 1,
    onDismiss,
    onGoToPayment
}) => {
    if (!isOpen) return null;

    const isTrial = type === 'trial_expiring';
    const isPlan = type === 'plan_expiring';
    const isReadOnly = type === 'read_only';

    // Gerador automático de títulos e mensagens dinâmicas por dias
    const getAlertContent = () => {
        if (isTrial) {
            if (daysLeft <= 0) {
                return {
                    badge: '⚠️ Termina Hoje',
                    badgeStyle: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
                    title: 'Seu teste gratuito termina hoje!',
                    message: 'Hoje é o último dia do seu teste. Após hoje, o app entrará em modo leitura. Acesse a tela de pagamento para continuar registrando suas corridas sem interrupções.',
                    ctaText: 'Ir para o Pagamento',
                    dismissText: 'Deixar para depois',
                    bullets: [
                        'Garante lançamentos ilimitados de corridas',
                        'Cálculo de lucro líquido real por KM e por Hora',
                        'Radar de voos e simulador de metas ativos'
                    ]
                };
            }
            if (daysLeft === 1) {
                return {
                    badge: '⏳ Último Dia Amanhã',
                    badgeStyle: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
                    title: 'Amanhã é seu último dia de teste!',
                    message: 'Seu período de teste gratuito termina amanhã. Não espere seu app travar: vá para o pagamento agora e garanta seu plano.',
                    ctaText: 'Ir para o Pagamento',
                    dismissText: 'Deixar para depois',
                    bullets: [
                        'Mantenha todas as suas ferramentas liberadas',
                        'Backup contínuo na nuvem',
                        'Planos acessíveis a partir de R$ 11,90'
                    ]
                };
            }
            return {
                badge: `⏳ Faltam ${daysLeft} dias de teste`,
                badgeStyle: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
                title: `Faltam ${daysLeft} dias para acabar seu teste`,
                message: `Você ainda tem ${daysLeft} dias de teste gratuito. Que tal já garantir sua assinatura para não ter que se preocupar no último dia?`,
                ctaText: 'Ir para o Pagamento',
                dismissText: 'Deixar para depois',
                bullets: [
                    'Aproveite todos os recursos liberados',
                    'Acesso completo sem anúncios ou bloqueios',
                    'Ativação imediata via Pix ou Cartão'
                ]
            };
        }

        if (isPlan) {
            if (daysLeft <= 0) {
                return {
                    badge: '⚠️ Vence Hoje',
                    badgeStyle: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
                    title: 'Sua assinatura Premium vence hoje!',
                    message: 'Hoje é a data de renovação da sua assinatura. Renove agora para evitar que seu aplicativo mude para modo leitura e bloqueie novas corridas.',
                    ctaText: 'Ir para o Pagamento',
                    dismissText: 'Deixar para depois',
                    bullets: [
                        'Evite interrupções no registro de corridas',
                        'Seus dados sincronizados e protegidos',
                        'Renovação rápida em poucos segundos'
                    ]
                };
            }
            if (daysLeft === 1) {
                return {
                    badge: '📅 Vence Amanhã',
                    badgeStyle: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
                    title: 'Sua assinatura vence amanhã!',
                    message: 'Falta apenas 1 dia para o vencimento do seu plano Premium. Renove com antecedência e continue trabalhando com tranquilidade.',
                    ctaText: 'Ir para o Pagamento',
                    dismissText: 'Deixar para depois',
                    bullets: [
                        'Garante que nenhuma corrida fique de fora',
                        'Histórico completo e relatórios salvos',
                        'Acesso sem limites'
                    ]
                };
            }
            return {
                badge: `📅 Vencimento em ${daysLeft} dias`,
                badgeStyle: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
                title: `Sua assinatura vence em ${daysLeft} dias`,
                message: `Sua assinatura Premium está próxima da data de vencimento. Acesse a tela de pagamento para renovar quando quiser.`,
                ctaText: 'Ir para o Pagamento',
                dismissText: 'Deixar para depois',
                bullets: [
                    'Renovação antecipada preserva todos os seus dias restantes',
                    'Total controle financeiro do seu veículo',
                    'Suporte prioritário'
                ]
            };
        }

        // Modo Leitura (Expirado)
        return {
            badge: '🔒 Modo Somente Leitura',
            badgeStyle: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30',
            title: 'Você está no Modo Somente Leitura',
            message: 'Seu período de acesso terminou. Você pode consultar todo o seu histórico, médias e relatórios passados normalmente, mas novos lançamentos de corridas e despesas estão bloqueados.',
            ctaText: 'Ir para o Pagamento',
            dismissText: 'Deixar para depois',
            bullets: [
                'Seus dados passados estão 100% seguros e acessíveis',
                'Para voltar a lançar corridas, ative o Premium',
                'Planos a partir de R$ 11,90'
            ]
        };
    };

    const content = getAlertContent();
    const IconComponent = isTrial ? Sparkles : isPlan ? Clock : BookOpen;
    const iconBg = isTrial 
        ? 'bg-gradient-to-br from-amber-500 to-yellow-600 text-white shadow-amber-500/25'
        : isPlan 
        ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-blue-500/25'
        : 'bg-gradient-to-br from-slate-700 to-gray-900 text-white shadow-gray-900/40';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md animate-in fade-in duration-200">
            <div className="relative w-full max-w-md max-h-[92vh] flex flex-col bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Botão de Fechar no topo */}
                <button
                    onClick={onDismiss}
                    aria-label="Fechar alerta"
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
                >
                    <X size={18} />
                </button>

                {/* Brilho suave de fundo */}
                <div className={`absolute -top-24 -left-24 w-48 h-48 rounded-full blur-3xl opacity-20 pointer-events-none ${
                    isTrial ? 'bg-amber-500' : isPlan ? 'bg-blue-600' : 'bg-rose-600'
                }`} />

                {/* Conteúdo com rolagem suave caso a tela do celular seja muito baixa */}
                <div className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 overflow-y-auto overscroll-contain">
                    {/* Cabeçalho */}
                    <div className="flex items-start gap-3">
                        <div className={`p-2.5 sm:p-3 rounded-2xl shadow-lg shrink-0 ${iconBg}`}>
                            <IconComponent size={22} className="sm:w-6 sm:h-6" />
                        </div>
                        <div className="space-y-1 pr-6 min-w-0">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-black uppercase tracking-wider border ${content.badgeStyle}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse shrink-0" />
                                <span className="truncate">{content.badge}</span>
                            </span>
                            <h3 className="text-base sm:text-lg font-black text-gray-900 dark:text-white tracking-tight leading-snug">
                                {content.title}
                            </h3>
                        </div>
                    </div>

                    {/* Mensagem Dinâmica Automática */}
                    <div className="p-3 sm:p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800/80 text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                        {content.message}
                    </div>

                    {/* Destaques (Bullets) */}
                    {content.bullets && (
                        <div className="space-y-1.5 sm:space-y-2 pt-0.5">
                            {content.bullets.map((bullet, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 font-medium">
                                    <div className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full flex items-center justify-center shrink-0 ${
                                        isTrial ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400' :
                                        isPlan ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400' :
                                        'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                                    }`}>
                                        <CheckCircle2 size={11} className="sm:w-3 sm:h-3" />
                                    </div>
                                    <span className="leading-tight">{bullet}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Botões de Ação */}
                    <div className="pt-1.5 space-y-2">
                        {/* Botão Primário: Ir para o Pagamento */}
                        <button
                            type="button"
                            onClick={() => {
                                onDismiss();
                                onGoToPayment();
                            }}
                            className={`w-full py-3 sm:py-3.5 px-4 sm:px-5 rounded-2xl text-xs sm:text-sm font-bold text-white shadow-lg flex items-center justify-center gap-2 sm:gap-2.5 transition-all active:scale-[0.98] ${
                                isTrial 
                                    ? 'bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 shadow-amber-500/25'
                                    : isPlan 
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/25'
                                    : 'bg-gradient-to-r from-gray-900 via-gray-800 to-black hover:bg-gray-800 shadow-gray-900/30'
                            }`}
                        >
                            <CreditCard size={16} className="shrink-0" />
                            <span className="tracking-wide">{content.ctaText}</span>
                            <ArrowRight size={16} className="shrink-0" />
                        </button>

                        {/* Botão Secundário: Deixar para depois */}
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="w-full py-2 sm:py-2.5 px-4 rounded-xl text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors text-center"
                        >
                            {content.dismissText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionAlertModal;
